import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Plane, CheckCircle2, MessageCircle, ArrowRight, Clock, ShieldCheck } from "lucide-react";
import usePageMeta from "../../hooks/usePageMeta";
import useJsonLd from "../../hooks/useJsonLd";
import { api, track } from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";
import { SearchBox, StickySearchCTA } from "../../components/b2c/SearchBox";
import { SectionHeading, FaqBlock, RouteCard, AirlineCard, fmtPKR } from "../../components/b2c/Blocks";
import { FareAdCard } from "../../components/b2c/FareAdCard";
import { OfferCard } from "../../components/b2c/OfferCard";
import { b2cPath } from "../../lib/host";

const WhatsAppCTA = ({ page, text }) => {
  const { settings } = useSettings();
  if (!settings?.whatsapp_number) return null;
  return (
    <a
      href={`https://wa.me/${settings.whatsapp_number}?text=${encodeURIComponent(text)}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="campaign-whatsapp-cta"
      onClick={() => track("whatsapp_click", page)}
      className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb757] text-white font-bold px-6 py-3 rounded-full transition-colors duration-200"
    >
      <MessageCircle className="w-4 h-4" /> Ask on WhatsApp
    </a>
  );
};

const NotFound = () => (
  <div className="max-w-3xl mx-auto px-4 py-32 text-left" data-testid="campaign-not-found">
    <h1 className="font-heading text-4xl font-bold text-slate-900">Page not found</h1>
    <p className="mt-3 text-slate-500">This deal page doesn't exist or is no longer active.</p>
    <Link to={b2cPath("") || "/"} className="inline-flex items-center gap-2 mt-6 text-[#970C10] font-semibold">
      Back to Farevault <ArrowRight className="w-4 h-4" />
    </Link>
  </div>
);

const AirlineView = ({ airline, related }) => {
  const page = `/flights/${airline.slug}`;
  const brand = airline.brand_color || "#970C10";
  return (
    <div>
      <section className="text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${brand} 0%, #032347 55%, #00172D 100%)` }}>
        <div className="h-1.5 w-full" style={{ background: `linear-gradient(90deg, ${brand} 50%, #970C10 50%)` }} data-testid="cobrand-stripe" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-10">
          <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="cobrand-lockup">
            <span className="inline-flex items-center gap-2 bg-white/95 rounded-full pl-2 pr-4 py-1.5">
              <span className="w-8 h-8 rounded-full flex items-center justify-center font-heading font-bold text-white text-xs" style={{ backgroundColor: brand }}>
                {airline.code || <Plane className="w-4 h-4" />}
              </span>
              <span className="font-heading font-bold text-sm text-slate-900">{airline.name}</span>
              <span className="text-slate-300">×</span>
              <span className="font-heading font-extrabold text-sm text-[#00172D]">
                sellv<span className="text-[#970C10]">{"\u25B2\uFE0E"}</span>go
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3.5 py-1.5 text-xs font-semibold" data-testid="authorised-badge">
              <ShieldCheck className="w-3.5 h-3.5" /> Authorised Booking Partner
            </span>
          </div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-white/70">{airline.tagline}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1" data-testid="airline-page-title">{airline.hero_headline}</h1>
          <p className="mt-3 text-white/80 max-w-2xl">{airline.hero_subtext}</p>
          <div className="mt-8">
            <SearchBox page={page} defaultDep={airline.default_dep} defaultRet={airline.default_ret} />
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-xs text-white/70">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Instant e-tickets via Sellvago's secure engine</span>
            <span className="flex items-center gap-1.5"><Plane className="w-3.5 h-3.5" /> Official multi-supplier {airline.name} fares</span>
          </div>
        </div>
      </section>

      <FareAdCard merchantCode={airline.code} airlineName={airline.name} origin={airline.default_dep} destination={airline.default_ret} />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <SectionHeading overline={`About ${airline.name}`} title={`Why book ${airline.name} with us`} />
          <p className="text-slate-600 leading-relaxed -mt-4">{airline.description}</p>
          <ul className="mt-8 grid sm:grid-cols-2 gap-4">
            {airline.highlights.map((h) => (
              <li key={h} className="flex items-start gap-3 text-sm text-slate-700 bg-white border border-slate-100 rounded-xl p-4">
                <CheckCircle2 className="w-5 h-5 text-[#970C10] shrink-0 mt-0.5" /> {h}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-slate-100 rounded-2xl p-6 h-fit">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-bold mb-4">Top destination offers</p>
          <div className="space-y-3" data-testid="airline-destinations">
            {airline.top_destinations.map((d) => (
              <OfferCard key={d.code} airline={airline} dest={d} page={page} />
            ))}
          </div>
          <div className="mt-5">
            <WhatsAppCTA page={page} text={`Hi, I'm looking for a ${airline.name} fare deal.`} />
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-16">
        <SectionHeading overline="Good to know" title={`${airline.name} booking FAQs`} />
        <FaqBlock faqs={airline.faqs} testId="airline-faq-accordion" />
      </section>

      {related.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16">
          <SectionHeading overline="Keep exploring" title="Popular routes" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {related.slice(0, 3).map((r) => <RouteCard key={r.slug} route={r} />)}
          </div>
        </section>
      )}
      <StickySearchCTA />
    </div>
  );
};

const RouteView = ({ route, relatedAirlines }) => {
  const page = `/flights/${route.slug}`;
  return (
    <div>
      <section className="bg-[#032347] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-[#E8767A] font-bold mb-3">
            {route.origin_code} → {route.dest_code} · {route.duration}
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight" data-testid="route-page-title">
            {route.origin_city} to {route.dest_city} flights from {fmtPKR(route.price_from)}
          </h1>
          <p className="mt-3 text-slate-300 max-w-2xl">{route.description}</p>
          <div className="mt-8">
            <SearchBox page={page} defaultDep={route.origin_code} defaultRet={route.dest_code} lockAirports />
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <SectionHeading overline="Route guide" title={`${route.origin_city} → ${route.dest_city} FAQs`} />
          <FaqBlock faqs={route.faqs} testId="route-faq-accordion" />
        </div>
        <div className="bg-white border border-slate-100 rounded-2xl p-6 h-fit">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-bold mb-4">Route snapshot</p>
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-3 text-slate-700">
              <Clock className="w-4 h-4 text-[#970C10]" /> Flight time: {route.duration}
            </div>
            <div className="flex items-start gap-3 text-slate-700">
              <Plane className="w-4 h-4 text-[#970C10] mt-0.5" />
              <span>Airlines: {route.airlines.join(", ")}</span>
            </div>
            <div className="flex items-center gap-3 text-slate-700">
              <span className="font-bold text-[#B01115]">from {fmtPKR(route.price_from)}</span> one way
            </div>
          </div>
          <div className="mt-6">
            <WhatsAppCTA page={page} text={`Hi, I need a fare for ${route.origin_city} to ${route.dest_city}.`} />
          </div>
        </div>
      </section>

      {relatedAirlines.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16">
          <SectionHeading overline="Airlines on this route" title="Compare airline deal pages" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {relatedAirlines.slice(0, 4).map((a) => <AirlineCard key={a.slug} airline={a} />)}
          </div>
        </section>
      )}
      <StickySearchCTA />
    </div>
  );
};

export default function CampaignPage() {
  const { slug } = useParams();
  const [airline, setAirline] = useState(null);
  const [route, setRoute] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [airlines, setAirlines] = useState([]);

  useEffect(() => {
    setAirline(null);
    setRoute(null);
    setNotFound(false);
    api.get(`/airlines/${slug}`)
      .then((r) => setAirline(r.data))
      .catch(() =>
        api.get(`/routes/${slug}`)
          .then((r) => setRoute(r.data))
          .catch(() => setNotFound(true))
      );
    api.get("/routes").then((r) => setRoutes(r.data)).catch(() => {});
    api.get("/airlines").then((r) => setAirlines(r.data)).catch(() => {});
    track("page_view", `/flights/${slug}`);
  }, [slug]);

  usePageMeta(
    airline?.seo_title || route?.seo_title,
    airline?.seo_description || route?.seo_description
  );

  const jsonLd = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://sellvago.com";
    const pageUrl = `${origin}/flights/${slug}`;
    if (airline) {
      const graph = [
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Flights", "item": `${origin}/flights` },
            { "@type": "ListItem", "position": 2, "name": airline.name, "item": pageUrl },
          ],
        },
        {
          "@type": "Airline",
          "name": airline.name,
          "iataCode": airline.code || undefined,
          "url": pageUrl,
          "description": airline.description,
        },
      ];
      if (airline.faqs?.length) {
        graph.push({
          "@type": "FAQPage",
          "mainEntity": airline.faqs.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        });
      }
      return { "@context": "https://schema.org", "@graph": graph };
    }
    if (route) {
      const graph = [
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Flights", "item": `${origin}/flights` },
            { "@type": "ListItem", "position": 2, "name": `${route.origin_city} to ${route.dest_city}`, "item": pageUrl },
          ],
        },
        {
          "@type": "Trip",
          "name": `${route.origin_city} to ${route.dest_city} flights`,
          "url": pageUrl,
          "description": route.description,
          "itinerary": [
            { "@type": "Place", "name": route.origin_city, "identifier": route.origin_code },
            { "@type": "Place", "name": route.dest_city, "identifier": route.dest_code },
          ],
          "offers": {
            "@type": "AggregateOffer",
            "priceCurrency": "PKR",
            "lowPrice": route.price_from,
            "offerCount": route.airlines?.length || 1,
            "url": pageUrl,
          },
        },
      ];
      if (route.faqs?.length) {
        graph.push({
          "@type": "FAQPage",
          "mainEntity": route.faqs.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        });
      }
      return { "@context": "https://schema.org", "@graph": graph };
    }
    return null;
  }, [airline, route, slug]);

  useJsonLd("campaign-page", jsonLd);

  if (notFound) return <NotFound />;
  if (airline) return <AirlineView airline={airline} related={routes} />;
  if (route) return <RouteView route={route} relatedAirlines={airlines} />;
  return (
    <div className="max-w-6xl mx-auto px-4 py-32" data-testid="campaign-loading">
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-slate-200 rounded w-1/2" />
        <div className="h-4 bg-slate-200 rounded w-2/3" />
        <div className="h-40 bg-slate-200 rounded-2xl" />
      </div>
    </div>
  );
}
